import type { Entity } from "@/components/data/entities";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import { Badge, Table, Tooltip } from "@onengine/component-library";
import type { TableColumn } from "@onengine/component-library";
import { Button } from "@/components/ui/Button/Button";
import { useEntitiesListWithFilters } from "@/hooks/entities/useEntitiesQueries";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { ENTITY_CATEGORY_OPTIONS, ENTITY_TYPE_OPTIONS } from "@/api/entities/entities.queries";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { useActivateEntity, useDeactivateEntity } from "@/hooks/entities/useEntitiesMutations";
import { getEntityStatusBadgeColors } from "@/utils/badgeConfig";
import { buildEntityWorkspaceUrl } from "@/utils/entityWorkspaceUrl";
import { Anchor, Box, Divider, Flex, Group, List, Menu, Stack, Text, Title } from "@mantine/core";
import {
  IconArrowsDiagonal,
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleMinus,
  IconEdit,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import TextWithConditionalTooltip from "@/components/HOC/TooltipHOC/TextWithConditionalTooltip";
import { useMemo, useState } from "react";
import styles from "./entities.module.scss";
import { useDisclosure } from "@mantine/hooks";
import type { EntityApiResponse } from "@/api/entities/entities.queries.types";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { authGuardWithPermissions } from "@/utils/authGuard";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";
import { useServerTable } from "@/hooks/table/useServerTable";
import type { TableFilterColumnConfig } from "@/hooks/table/useTableFilterOptions";
import {
  InlineTableCell,
  renderBadgeCell,
  renderTextCell,
  RowActionsCell,
} from "@/components/table/tableCells";
import { applyColumnFilters } from "@/components/table/columnFilters";

export const Route = createFileRoute("/_authenticated/entities/")({
  component: Entities,
  validateSearch: tableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const formatWebsite = (website: string) => {
  if (!website) return "";
  return website.replace(/^https?:\/\//, "");
};

const NAME_CELL_MAX_LENGTH = 30;

const NameCell = ({ name }: { name: string }) => (
  <TextWithConditionalTooltip
    text={name}
    maxLength={NAME_CELL_MAX_LENGTH}
    isMediaQueryRequired={false}
    style={{ fontWeight: "var(--font-weight-medium)" }}
  />
);

const WebsiteCell = ({ website }: { website: string }) => {
  const MAX_LENGTH = 50;
  const truncatedWebsite =
    website.length > MAX_LENGTH ? `${website.substring(0, MAX_LENGTH)}...` : website;
  const shouldShowTooltip = website.length > MAX_LENGTH;

  const anchorContent = (
    <Anchor
      href={`https://${website}`}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.websiteLink}
      onClick={(e) => e.stopPropagation()}
    >
      {truncatedWebsite}
    </Anchor>
  );

  if (shouldShowTooltip) {
    return (
      <Tooltip content={formatWebsite(website || "demo-entity.com")} pointer="top-center">
        {anchorContent}
      </Tooltip>
    );
  }

  return anchorContent;
};

const SubdomainCell = ({ subDomain }: { subDomain: string }) => {
  const url = buildEntityWorkspaceUrl(subDomain);
  const display = url.replace(/^https?:\/\//, "").replace(/\.servengine\.com$/, "");

  return (
    <Anchor
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.websiteLink}
      onClick={(e) => e.stopPropagation()}
    >
      {display}
    </Anchor>
  );
};

const HQAddressCell = ({ address }: { address: string }) => {
  if (!address) return <span className={styles.hqAddressPlaceholder}>—</span>;
  return (
    <span className={styles.hqAddress}>
      <span>{address}</span>
    </span>
  );
};

const formatAddress = (hqAddress: EntityApiResponse["hqAddress"]): string => {
  if (!hqAddress) return "";
  const parts = [hqAddress.street, hqAddress.city, hqAddress.state, hqAddress.zip].filter(Boolean);
  return parts.join(", ");
};

const mapEntityApiToEntity = (apiEntity: EntityApiResponse, index: number): Entity => {
  return {
    id: apiEntity.id ?? index + 1,
    name: apiEntity.entityName,
    entityType: apiEntity.entityType?.toLowerCase() === "internal" ? "Internal" : "External",
    entityCategory: apiEntity.category,
    entityStatus: apiEntity?.status,
    ein: apiEntity.ein ?? "",
    website: apiEntity.website?.replace(/^https?:\/\//, "") || "", // Remove protocol
    subDomain: apiEntity.subDomain ?? "",
    hqAddress: formatAddress(apiEntity.hqAddress),
    pocAdminName:
      [apiEntity?.pocFirstName, apiEntity?.pocLastName].filter(Boolean).join(" ") ||
      apiEntity?.pocAdminName ||
      "—",
    pocPhoneNumber: apiEntity?.pocPhoneNumber ?? "—",
    pocAdminEmail: apiEntity?.pocAdminEmail ?? "—",
  };
};

const getSortByField = (columnId: string): string | undefined => {
  const fieldMap: Record<string, string> = {
    name: "entityName",
    entityType: "entityType",
    entityCategory: "category",
    entityStatus: "status",
    ein: "ein",
    website: "website",
    hqAddress: "hqAddress",
    pocAdminName: "pocAdminName",
    pocPhoneNumber: "pocPhoneNumber",
    pocAdminEmail: "pocAdminEmail",
  };
  return fieldMap[columnId];
};

const ENTITIES_FILTER_COLUMNS: TableFilterColumnConfig[] = [
  {
    columnId: "name",
    filterByKey: "entityName",
    storeType: "entities",
    searchParams: { type: "entity", fields: ["label"], limit: 10, key: "entityName" },
  },
  // Type and Category are a small fixed set, so the shared option constants are used as static
  // items and the chip sends their value rather than the dropdown's row id. Type is validated
  // against an enum and an unknown value is rejected; category is not, so an unknown one is
  // accepted and quietly matches nothing.
  {
    columnId: "entityType",
    filterByKey: "entityType",
    staticItems: ENTITY_TYPE_OPTIONS.map((o) => ({ id: o.value, label: o.label })),
  },
  {
    columnId: "entityCategory",
    filterByKey: "category",
    staticItems: ENTITY_CATEGORY_OPTIONS.map((o) => ({ id: o.value, label: o.label })),
  },
  {
    columnId: "hqAddress",
    filterByKey: "hqAddress",
    storeType: "entities",
    // `value` is the combined "City, ST" the dropdown builds via its modify param;
    // labelling by it keeps each option unique (bare city names collide in the chip).
    searchParams: { type: "GeographyCities", fields: ["value"], limit: 10, key: "hqAddress" },
  },
];

const deactivateEntityModalData = [
  {
    heading: "Deactivation shall impact the following items, but are not limited to:",
    boldHeading: true,
    items: [
      "Active work orders",
      "Pending AP / AR",
      "Assigned users",
      "Active service providers",
      "Active integrations",
      "Compliance or audit flags",
    ],
  },
  {
    heading: "Upon deactivating,",
    boldHeading: false,
    items: [
      "All associated data will remain preserved for audit and record purposes.",
      "You can reactivate it later if needed.",
    ],
  },
];

const activateEntityModalData = [
  {
    heading: "Activation shall restore access to the following, but is not limited to:",
    boldHeading: true,
    items: [
      "Work order management",
      "AP / AR processing",
      "Assigned users",
      "Service provider associations",
      "Active integrations",
      "Compliance and audit tracking",
    ],
  },
  {
    heading: "Upon activating,",
    boldHeading: false,
    items: [
      "All previously preserved data will be accessible again.",
      "You can deactivate it again at any time if needed.",
    ],
  },
];

const EntityStatusModalBody = ({
  entityName,
  action,
}: {
  entityName: string;
  action: "activate" | "deactivate";
}) => {
  const modalData = action === "activate" ? activateEntityModalData : deactivateEntityModalData;
  const actionLabel = action === "activate" ? "activate" : "deactivate";

  return (
    <Stack gap={"var(--spacing-small)"} w="100%">
      <Text size="sm" c="var(--text-default)">
        This action is going to {actionLabel} the following Entity{" "}
        <Text component="span" fw={"var(--font-weight-semi-bold)"}>
          &quot;{entityName}&quot;
        </Text>
        .
      </Text>

      <Divider style={{ borderColor: "var(--border-default)" }} />

      {modalData.map((section) => (
        <Box key={section.heading} mb={"var(--spacing-3x-small)"}>
          <Text
            size="sm"
            fw={section.boldHeading ? "var(--font-weight-semi-bold)" : undefined}
            c="var(--text-default)"
          >
            {section.heading}
          </Text>
          <List size="sm" c="var(--text-default)" pl="md">
            {section.items.map((item) => (
              <List.Item key={item}>{item}</List.Item>
            ))}
          </List>
        </Box>
      ))}

      <Divider style={{ borderColor: "var(--border-default)" }} />

      <Text size="sm" fw={"var(--font-weight-semi-bold)"} c="var(--text-neutral-default)">
        Are you sure you want to continue?
      </Text>
    </Stack>
  );
};

function Entities() {
  const navigate = useNavigate();

  const [deactivateOpened, { open: openDeactivate, close: closeDeactivate }] = useDisclosure(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [entityStatusChange, setEntityStatusChange] = useState("deactivate");

  const { mutate: deactivateEntityMutate, isPending: isDeactivating } = useDeactivateEntity();
  const { mutate: activateEntityMutate, isPending: isActivating } = useActivateEntity();

  useBreadcrumb([
    {
      title: "Entities",
      href: "/entities",
      icon: getRouteIcon(MODULE_KEYS_MAP.entities),
    },
  ]);

  const searchParams = useSearch({ from: "/_authenticated/entities/" });

  const { queryOptions, isPrefsLoading, controlProps, tableKey } = useServerTable({
    viewName: "entityList",
    columns: ENTITIES_FILTER_COLUMNS,
    getSortByField,
    enableUrlSync: true,
    searchParams,
  });

  const {
    data: entitiesData,
    isPending: isEntitiesLoading,
    isFetching,
  } = useEntitiesListWithFilters({
    page: queryOptions.page,
    pageSize: queryOptions.pageSize,
    search: queryOptions.search,
    sortBy: queryOptions.sortBy,
    sortOrder: queryOptions.sortOrder,
    filters: queryOptions.filters,
  });

  const tableData = useMemo<Entity[]>(() => {
    if (!entitiesData?.data?.data) return [];
    return entitiesData.data.data.map(mapEntityApiToEntity);
  }, [entitiesData]);

  const columns = useMemo<TableColumn<Entity>[]>(
    () =>
      applyColumnFilters<Entity>(
        [
          {
            key: "name",
            label: "Entities Name",
            sortable: true,
            render: (row) => renderTextCell(<NameCell name={row.name} />),
          },
          {
            key: "entityType",
            label: "Entity Type",
            sortable: true,
            render: (row) =>
              renderBadgeCell(
                <Badge
                  color={row.entityType === "Internal" ? "indigo" : "iris"}
                  variant="lowContrast"
                >
                  {row.entityType}
                </Badge>,
              ),
          },
          {
            key: "entityCategory",
            label: "Entity Category",
            sortable: true,
            render: (row) => {
              const val = row.entityCategory || "";
              return renderTextCell(val ? val.charAt(0).toUpperCase() + val.slice(1) : "—");
            },
          },
          {
            key: "entityStatus",
            label: "Entity Status",
            sortable: true,
            render: (row) => {
              const status = row.entityStatus || "";
              if (!status) return renderTextCell("—");
              const label = status.charAt(0).toUpperCase() + status.slice(1);
              const { color, variant } = getEntityStatusBadgeColors(status);
              const icon =
                status === "active" ? (
                  <IconCircleCheckFilled size={20} />
                ) : (
                  <IconCircleMinus size={20} />
                );
              return renderBadgeCell(
                <Badge color={color} variant={variant} iconLeft={icon}>
                  {label}
                </Badge>,
              );
            },
          },
          {
            key: "ein",
            label: "EIN",
            sortable: true,
            render: (row) => renderTextCell(row.ein || "—"),
          },
          {
            key: "website",
            label: "Website",
            sortable: true,
            render: (row) =>
              row.website ? (
                <InlineTableCell>
                  <WebsiteCell website={row.website} />
                </InlineTableCell>
              ) : (
                renderTextCell("—")
              ),
          },
          {
            key: "subDomain",
            label: "Subdomain",
            sortable: false,
            render: (row) =>
              row.subDomain ? (
                <InlineTableCell>
                  <SubdomainCell subDomain={row.subDomain} />
                </InlineTableCell>
              ) : (
                renderTextCell("—")
              ),
          },
          {
            key: "hqAddress",
            label: "HQ Address",
            sortable: true,
            render: (row) => (
              <InlineTableCell>
                <HQAddressCell address={row.hqAddress} />
              </InlineTableCell>
            ),
          },
          {
            key: "pocAdminName",
            label: "POC Name",
            sortable: true,
            render: (row) => renderTextCell(row.pocAdminName || "—"),
          },
          {
            key: "pocPhoneNumber",
            label: "Phone Number",
            sortable: true,
            render: (row) => renderTextCell(row.pocPhoneNumber || "—"),
          },
          {
            key: "pocAdminEmail",
            label: "Email",
            sortable: true,
            render: (row) => renderTextCell(row.pocAdminEmail || "—"),
          },
          {
            id: "more",
            key: "id",
            label: "",
            sortable: false,
            pinRight: true,
            width: "60px",
            render: (row) => {
              const entityId = String(row.id);
              return (
                <RowActionsCell>
                  <Menu.Item
                    disabled={isActivating || isDeactivating}
                    leftSection={<IconArrowsDiagonal size={20} />}
                    onClick={() => navigate({ to: "/entities/$entityId", params: { entityId } })}
                  >
                    View Entity Settings
                  </Menu.Item>
                  <Menu.Item
                    disabled={isActivating || isDeactivating}
                    leftSection={<IconEdit size={20} />}
                    onClick={() => navigate({ to: "/entities/$entityId", params: { entityId } })}
                  >
                    Edit Entity Details
                  </Menu.Item>
                  <Menu.Item
                    disabled={isActivating || isDeactivating}
                    leftSection={
                      row.entityStatus === "inactive" ? (
                        <IconCircleCheck size={20} />
                      ) : (
                        <IconTrash size={20} />
                      )
                    }
                    c={
                      row.entityStatus === "inactive"
                        ? "var(--text-success-default)"
                        : "var(--text-danger-default)"
                    }
                    onClick={() => {
                      setSelectedEntity(row);
                      setEntityStatusChange(
                        row.entityStatus === "inactive" ? "activate" : "deactivate",
                      );
                      openDeactivate();
                    }}
                  >
                    {row.entityStatus === "inactive" ? "Activate Entity" : "Deactivate Entity"}
                  </Menu.Item>
                </RowActionsCell>
              );
            },
          },
        ],
        ENTITIES_FILTER_COLUMNS,
      ),
    [navigate, openDeactivate, isActivating, isDeactivating],
  );

  const handleDeactivateConfirm = () => {
    if (!selectedEntity) return;
    deactivateEntityMutate(String(selectedEntity.id), {
      onSuccess: () => {
        closeDeactivate();
        handleApiSuccess(
          `"${selectedEntity.name}" has been successfully deactivated.`,
          "Entity Deactivated",
        );
      },
      onError: () => {
        showErrorAlert(
          `Failed to deactivate "${selectedEntity.name}". Please try again.`,
          "Deactivation Failed",
        );
      },
    });
  };

  const handleActivateConfirm = () => {
    if (!selectedEntity) return;
    activateEntityMutate(String(selectedEntity.id), {
      onSuccess: () => {
        closeDeactivate();
        handleApiSuccess(
          `"${selectedEntity.name}" has been successfully activated.`,
          "Entity Activated",
        );
      },
      onError: () => {
        showErrorAlert(
          `Failed to activate "${selectedEntity.name}". Please try again.`,
          "Activation Failed",
        );
      },
    });
  };

  return (
    <>
      <Flex
        direction="column"
        style={{
          margin: "calc(var(--mantine-spacing-md) * -1)",
          padding: "var(--spacing-medium)",
          backgroundColor: "var(--neutral-500)",
        }}
      >
        <Group
          justify="space-between"
          align="center"
          style={{ marginBottom: "var(--spacing-medium)" }}
        >
          <Title
            order={3}
            style={{
              fontWeight: "var(--font-weight-bold)",
              fontSize: "var(--heading-font-size-h3)",
            }}
          >
            Entities
          </Title>
          <Button
            variant="primary"
            iconLeft={<IconPlus size={16} />}
            onClick={() => navigate({ to: "/entities/create-entity" })}
          >
            New Entity
          </Button>
        </Group>
        <Table<Entity>
          key={tableKey}
          {...controlProps}
          columns={columns}
          data={tableData}
          loading={isFetching || isPrefsLoading || isEntitiesLoading}
          totalCount={entitiesData?.data?.total ?? 0}
          cellClickIgnoreColumns={["more"]}
          onRowClick={(row) =>
            navigate({ to: "/entities/$entityId", params: { entityId: String(row.id) } })
          }
          emptyText="No entities found"
        />
      </Flex>
      <ConfirmationModal
        isOpen={deactivateOpened}
        onClose={closeDeactivate}
        title={entityStatusChange === "activate" ? "Activate Entity" : "Deactivate Entity"}
        body={
          <EntityStatusModalBody
            entityName={selectedEntity?.name ?? ""}
            action={entityStatusChange as "activate" | "deactivate"}
          />
        }
        confirmText={
          entityStatusChange === "activate" ? "Yes, Activate Entity" : "Yes, Deactivate Entity"
        }
        cancelText="Cancel"
        onConfirm={
          entityStatusChange === "activate" ? handleActivateConfirm : handleDeactivateConfirm
        }
        onCancel={closeDeactivate}
        confirmVariant={entityStatusChange === "activate" ? "primary" : "destructive-primary"}
        isConfirmLoading={isDeactivating || isActivating}
        size="lg"
      />
    </>
  );
}
